/**
 * AnnouncementsPage — CVG3-NOTIF (L104 addendum, 2026-07-11).
 *
 * Admin/owner composer for the tenant announcement (the CVG2 `homeThought.pin`
 * grown with placement). Message + end date + dismissable + WHERE it shows:
 *   - After login  → every rep's Home thought strip + hot-prominence phone ping
 *   - Before login → read-only banner on the login screen (no ping — nobody's
 *                    logged in to receive one)
 *   - Both
 *
 * Uses its OWN scoped PUT ({ homeThought: { pin } }) via api/announcements.ts —
 * never the page-wide merged-config save (the backend schema is .strict();
 * known CVG2 bottom-bar flag, deliberately not fixed here).
 *
 * Design: AURA design tokens only (var(--saffron), var(--ink-1), var(--line)…).
 */
import { useEffect, useState } from 'react';
import { Megaphone, Save, CheckCircle2, Trash2, BellRing, EyeOff } from 'lucide-react';
import {
  fetchAnnouncementPin,
  saveAnnouncementPin,
  type AnnouncementPin,
  type AnnouncementPlacement,
} from '../../api/announcements';

const EMPTY: AnnouncementPin = {
  message: '',
  endAt: null,
  dismissable: true,
  placement: 'post_login',
};

/** ISO → value for <input type="datetime-local"> in the browser's timezone. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO (null when cleared). */
function localInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const PLACEMENT_OPTIONS: Array<{
  value: AnnouncementPlacement;
  label: string;
  hint: string;
}> = [
  {
    value: 'post_login',
    label: 'After login',
    hint: 'Home thought strip for every rep + phone ping at full prominence.',
  },
  {
    value: 'pre_login',
    label: 'Before login',
    hint: 'Banner on the app login screen. No phone ping (nobody is logged in yet).',
  },
  {
    value: 'both',
    label: 'Both',
    hint: 'Login-screen banner AND Home strip + phone ping.',
  },
];

export function AnnouncementsPage() {
  const [pin, setPin] = useState<AnnouncementPin>(EMPTY);
  const [hasActivePin, setHasActivePin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const current = await fetchAnnouncementPin();
        if (current) {
          setPin(current);
          setHasActivePin(true);
        }
      } catch {
        /* defaults stand */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const send = async () => {
    const message = pin.message.trim();
    if (!message) return;
    setSaving(true);
    setError(null);
    try {
      await saveAnnouncementPin({ ...pin, message });
      setHasActivePin(true);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Could not send the announcement. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAnnouncementPin(null);
      setPin(EMPTY);
      setHasActivePin(false);
    } catch {
      setError('Could not clear the announcement. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    borderColor: 'var(--line)',
    background: 'var(--ivory-0)',
    color: 'var(--ink-1)',
  } as const;

  const willPing = pin.placement !== 'pre_login';

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-1">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--saffron-soft, #FFF3E0)' }}
        >
          <Megaphone size={20} style={{ color: 'var(--saffron)' }} />
        </div>
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--ink-1)' }}>
            Announcements
          </h1>
          <p className="text-sm" style={{ color: 'var(--ink-3, var(--ink-2))' }}>
            One pinned message for the whole team — attributed “From ‹owner›”.
          </p>
        </div>
      </div>

      <div
        className="mt-5 rounded-2xl border p-5 space-y-5"
        style={{ borderColor: 'var(--line)', background: 'var(--ivory-1, #fff)' }}
      >
        {loading ? (
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>Loading…</p>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-1)' }}>
                Message
              </label>
              <textarea
                value={pin.message}
                onChange={(e) => setPin((p) => ({ ...p, message: e.target.value }))}
                maxLength={300}
                rows={3}
                placeholder="e.g. Walk-in mela this Saturday — tell every customer!"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={inputStyle}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--ink-2)' }}>
                Sending a new or edited message re-shows it to reps who dismissed the old one
                {willPing ? ' and pings every rep’s phone at full prominence.' : '.'}
              </p>
            </div>

            <div>
              <span className="block text-sm font-medium mb-2" style={{ color: 'var(--ink-1)' }}>
                Where it shows
              </span>
              <div className="space-y-2">
                {PLACEMENT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-start gap-2 cursor-pointer rounded-lg border px-3 py-2"
                    style={{
                      borderColor: pin.placement === opt.value ? 'var(--saffron)' : 'var(--line)',
                      background: 'var(--ivory-0)',
                    }}
                  >
                    <input
                      type="radio"
                      name="placement"
                      value={opt.value}
                      checked={pin.placement === opt.value}
                      onChange={() => setPin((p) => ({ ...p, placement: opt.value }))}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                        {opt.label}
                      </span>
                      <span className="block text-xs" style={{ color: 'var(--ink-2)' }}>
                        {opt.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-1)' }}>
                  Ends
                </label>
                <input
                  type="datetime-local"
                  value={isoToLocalInput(pin.endAt)}
                  onChange={(e) => setPin((p) => ({ ...p, endAt: localInputToIso(e.target.value) }))}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={inputStyle}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--ink-2)' }}>
                  Disappears automatically after this time. Empty = stays until cleared.
                </p>
              </div>
              <div>
                <span className="block text-sm font-medium mb-1" style={{ color: 'var(--ink-1)' }}>
                  Reps can dismiss
                </span>
                <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--ink-1)' }}>
                  <input
                    type="checkbox"
                    checked={pin.dismissable}
                    onChange={(e) => setPin((p) => ({ ...p, dismissable: e.target.checked }))}
                  />
                  Each rep can hide it for themselves
                </label>
              </div>
            </div>

            <div
              className="flex items-center gap-2 text-xs rounded-lg px-3 py-2"
              style={{ background: 'var(--ivory-0)', color: 'var(--ink-2)' }}
            >
              {willPing ? <BellRing size={14} /> : <EyeOff size={14} />}
              {willPing
                ? 'Phone ping fires once per new message — re-saving the same message never re-pings.'
                : 'Before-login only: shown on the login screen, no phone ping.'}
            </div>

            {error && (
              <p className="text-sm" style={{ color: 'var(--wine, #b91c1c)' }}>{error}</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              {hasActivePin && (
                <button
                  type="button"
                  onClick={clear}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border disabled:opacity-60"
                  style={{ borderColor: 'var(--line)', color: 'var(--ink-1)' }}
                >
                  <Trash2 size={14} />
                  Clear announcement
                </button>
              )}
              <button
                type="button"
                onClick={send}
                disabled={saving || pin.message.trim() === ''}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                style={{ background: 'var(--saffron)' }}
              >
                {saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
                {saving ? 'Sending…' : saved ? 'Sent!' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
